/**
 * The model's own cable figures in a reply, checked against the editor's. The model often states
 * a cable capacity ("Iz ≈ 37 A for 4 mm²") or a max rating ("20 A admissible on 1.5 mm²"), and
 * those figures are sometimes wrong. The proposal already shows the editor's ΔU and Iz; this
 * corrects the same figures in the reply's text, so the user never reads a wrong one:
 *
 * - "Iz <n> A" next to a section → the editor's Iz for that section (IEC 60364-5-52 table of
 *   @elec/calc), with the installation method and cable type the sentence names, else those of
 *   the circuit it names, else the project's usual ones (method B1 when none is set, as the
 *   editor);
 * - "max <n> A", "<n> A max / admissible", "au plus / jusqu'à / maximaal <n> A" next to a section
 *   → the max rating for that section (MAX_RATING_A, the rule pack's table).
 *
 * Only a figure with a section in the same sentence is touched; everything else is left as the
 * model wrote it. A design current ("Ib ≈ 20 A max") is never read as a max rating.
 */
import type { Locale, Project } from '@elec/domain';
import { analyse } from '@elec/analysis';
import { currentCapacity, insulationOf, INSTALL_METHODS, type InstallMethod } from '@elec/calc';
import { MAX_RATING_A } from '@elec/rules';

export interface FigureFix {
  kind: 'iz' | 'max-rating';
  sectionMm2: number;
  /** What the model wrote and what the editor computes (A). */
  claimed: number;
  editor: number;
}

const SECTIONS = new Set([1.5, 2.5, 4, 6, 10, 16, 25]);
const NUM = String.raw`(\d{1,3}(?:[.,]\d{1,2})?)`;
const toNum = (s: string) => Number(s.replace(',', '.'));

/** "2,5 mm²", "4 mm2", "3G1,5", "5G6": a copper section, and whether the cable is 5-core. */
const SECTION_RE = new RegExp(
  String.raw`\b(?:([35])G\s?)?(\d{1,2}(?:[.,]\d)?)\s*mm(?:²|2)(?![\w])|\b([35])G(\d{1,2}(?:[.,]\d)?)\b`,
  'gi',
);
/** "Iz 31 A", "Iz ≈ 37 A", "Iz = 20 A", "capacité Iz de 63 A", "Iz van 31 A". */
const IZ_RE = new RegExp(String.raw`\bIz\b(\s*(?:de|van|of|is|est de|:|=)?\s*)(≈|~|=)?\s*${NUM}\s*A\b`, 'g');
/** A max rating: "max 20 A", "au plus 20 A", "jusqu'à 40 A", "maximaal 20 A", "at most / up to 20 A". */
const MAX_BEFORE = new RegExp(
  String.raw`\b(max(?:imum|imal|imale|imaal)?|au plus|jusqu['’]à|hoogstens|at most|up to)(\s+(?:de\s+|of\s+|van\s+)?)${NUM}\s*A\b`,
  'gi',
);
/** "20 A max", "16 A maximum", "20 A admissible", "16 A toegelaten / toelaatbaar / toegestaan". */
const MAX_AFTER = new RegExp(
  String.raw`(?<![\w,.])${NUM}(\s*A\s+)(max(?:imum)?\b|admissibles?\b|toelaatbaar\b|toegelaten\b|toegestaan\b)`,
  'gi',
);
/** A design or rated current just before the number: "Ib ≈ 20 A max" is not a max rating. */
const CURRENT_BEFORE = /\b(Ib|In|Iz)\s*(≈|~|=|:|de|van)?\s*$/i;
const METHOD_RE = /\b(?:m[ée]thode|method|methode|mode)\s+(A1|B1|C)\b|\b(?:en|in)\s+(A1|B1)\b|\((A1|B1|C)\)/i;
const CABLE_RE = /\b(e?xvb|xgb|vob|vvb|vtmb|r2v|u1000|h07v-?[ru]|ymvk|vmvk|xmvk|nym)\b/i;
const CIRCUIT_RE = /\b(?:circuit|kring|stroomkring)\s+([A-Z]\d{0,2})\b/;
const THREE_PHASE = /\b5G|triphas|three-phase|driefasig|3 ?x ?400|400 ?V/i;

/** Sentences, keeping their separators: split on . ! ? ; and new lines, never inside "2.5". */
function sentences(text: string): string[] {
  return text.split(/((?<!\d)[.!?;](?!\d)|\n)/);
}

interface Defaults {
  method: InstallMethod;
  cableType: string;
  /** Per circuit code (A, B, C1…): its method, cable type and phases. */
  circuits: Map<string, { method: InstallMethod; cableType: string; threePhase: boolean }>;
}

function defaultsOf(project: Project): Defaults {
  const circuits: Defaults['circuits'] = new Map();
  const methods = new Map<InstallMethod, number>();
  const types = new Map<string, number>();
  for (const c of analyse(project).circuits) {
    const m = c.circuit?.cable.installMethod;
    const method = (INSTALL_METHODS as readonly string[]).includes(m ?? '') ? (m as InstallMethod) : 'B1';
    methods.set(method, (methods.get(method) ?? 0) + 1);
    if (c.cable.type) types.set(c.cable.type, (types.get(c.cable.type) ?? 0) + 1);
    if (c.code)
      circuits.set(c.code, {
        method,
        cableType: c.cable.type,
        threePhase: (c.cable.cores ?? 3) >= 5 || /^5G/i.test(c.cable.type),
      });
  }
  const top = <K>(m: Map<K, number>) => [...m].sort((a, b) => b[1] - a[1])[0]?.[0];
  const standardCable: Record<string, string> = { rgie: 'XVB', nfc15100: 'R2V', nen1010: 'YMvK' };
  return {
    method: top(methods) ?? 'B1',
    cableType: top(types) ?? standardCable[project.meta.standard ?? 'rgie'] ?? 'XVB',
    circuits,
  };
}

function fmt(v: number, locale: Locale) {
  const s = String(Math.round(v * 10) / 10);
  return locale === 'en' ? s : s.replace('.', ',');
}

/** The section mentioned nearest to `at` in the sentence, if any. */
function nearestSection(sentence: string, at: number): { mm2: number; fiveCore: boolean } | undefined {
  let best: { mm2: number; fiveCore: boolean; d: number } | undefined;
  for (const m of sentence.matchAll(SECTION_RE)) {
    const mm2 = toNum(m[2] ?? m[4] ?? '');
    if (!SECTIONS.has(mm2)) continue;
    const d = Math.abs(m.index - at);
    if (!best || d < best.d) best = { mm2, fiveCore: (m[1] ?? m[3]) === '5', d };
  }
  return best;
}

/** The reply with the model's Iz and max-rating figures replaced by the editor's. */
export function correctFigures(
  reply: string,
  project: Project,
  locale: Locale,
): { reply: string; fixes: FigureFix[] } {
  if (
    !/\bIz\b|max|admissible|au plus|jusqu|toegelaten|toelaatbaar|toegestaan|hoogstens|at most|up to/i.test(
      reply,
    )
  )
    return { reply, fixes: [] };
  const d = defaultsOf(project);
  const fixes: FigureFix[] = [];
  const out = sentences(reply).map((s) => {
    if (!SECTION_RE.test(s)) return s;
    SECTION_RE.lastIndex = 0;
    const named = CIRCUIT_RE.exec(s)?.[1];
    const circuit = named ? d.circuits.get(named) : undefined;
    const mm = METHOD_RE.exec(s);
    const method = ((mm && (mm[1] ?? mm[2] ?? mm[3])?.toUpperCase()) ??
      circuit?.method ??
      d.method) as InstallMethod;
    const cableType = CABLE_RE.exec(s)?.[1] ?? circuit?.cableType ?? d.cableType;
    const insulation = insulationOf(cableType);
    // Iz
    let t = s.replace(IZ_RE, (all, sep: string, approx: string | undefined, n: string, at: number) => {
      const sec = nearestSection(s, at);
      if (!sec) return all;
      const loaded = sec.fiveCore || THREE_PHASE.test(s) || circuit?.threePhase ? 3 : 2;
      const iz = currentCapacity(sec.mm2, method, insulation, loaded);
      const claimed = toNum(n);
      if (iz === undefined || Math.abs(iz - claimed) < 1) return all;
      fixes.push({ kind: 'iz', sectionMm2: sec.mm2, claimed, editor: iz });
      return `Iz${approx ? ' ' : sep || ' '}${fmt(iz, locale)} A`;
    });
    // Max rating
    const maxFix = (all: string, n: string, at: number, rebuild: (v: string) => string) => {
      if (CURRENT_BEFORE.test(t.slice(Math.max(0, at - 8), at))) return all;
      const sec = nearestSection(t, at);
      const max = sec ? MAX_RATING_A[String(sec.mm2)] : undefined;
      const claimed = toNum(n);
      if (!sec || max === undefined || max === claimed) return all;
      fixes.push({ kind: 'max-rating', sectionMm2: sec.mm2, claimed, editor: max });
      return rebuild(String(max));
    };
    t = t.replace(MAX_BEFORE, (all, word: string, gap: string, n: string, at: number) =>
      maxFix(all, n, at + all.length - `${n}`.length - 2, (v) => `${word}${gap}${v} A`),
    );
    t = t.replace(MAX_AFTER, (all, n: string, gap: string, word: string, at: number) =>
      maxFix(all, n, at, (v) => `${v}${gap}${word}`),
    );
    return t;
  });
  return { reply: out.join(''), fixes };
}
